package com.partejar.app.feature.gestantes.presentation

import com.partejar.app.core.ui.domain.address.PatientAddressFields

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Checkbox
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarDateField
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteClinicalUpdate

@Composable
internal fun GestanteEditAction(
    saving: Boolean,
    onClick: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Surface(
        onClick = onClick,
        enabled = !saving,
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 60.dp),
        shape = MaterialTheme.shapes.extraLarge,
        color = MaterialTheme.colorScheme.surfaceContainerLow,
        contentColor = MaterialTheme.colorScheme.primary,
        border = BorderStroke(
            dimensions.strokeThin,
            if (saving) MaterialTheme.colorScheme.outlineVariant else MaterialTheme.colorScheme.primary.copy(alpha = 0.62f)
        )
    ) {
        Row(
            modifier = Modifier.padding(horizontal = spacing.space20, vertical = spacing.space16),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = PartejarIcons.Actions.edit,
                contentDescription = null,
                modifier = Modifier.size(dimensions.iconMedium)
            )
            Text(
                text = "Editar dados",
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.titleSmall
            )
            Icon(
                imageVector = PartejarIcons.Actions.next,
                contentDescription = null,
                modifier = Modifier.size(dimensions.iconMedium)
            )
        }
    }
}

@Composable
internal fun EditClinicalData(
    detail: GestanteClinicalDetail,
    saving: Boolean,
    onSave: (GestanteClinicalUpdate) -> Unit,
    onCancel: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    var babyName by remember(detail.babyName) { mutableStateOf(detail.babyName.orEmpty()) }
    var dueDate by remember(detail.dueDate) { mutableStateOf(formatBrazilianDate(detail.dueDate).orEmpty()) }
    var attemptedSave by remember(detail.id) { mutableStateOf(false) }
    var maternity by remember(detail.maternity) { mutableStateOf(detail.maternity.orEmpty()) }
    var prenatalType by remember(detail.prenatalType) { mutableStateOf(detail.prenatalType) }
    var livesInBh by remember(detail.livesInBh) { mutableStateOf(detail.livesInBh) }
    var address by remember(detail.address) { mutableStateOf(detail.address) }

    PartejarCard {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
            Text("Dados obstétricos", style = MaterialTheme.typography.titleMedium)
            PartejarTextField(babyName, { babyName = it }, label = { Text("Nome do bebê") }, enabled = !saving)
            PartejarDateField(
                value = dueDate,
                onValueChange = { dueDate = it },
                label = "DPP",
                enabled = !saving,
                error = if (attemptedSave && dueDate.isNotBlank() && parseBrazilianDate(dueDate) == null)
                    "Informe uma data válida no formato dd/MM/aaaa." else null
            )
            PartejarTextField(maternity, { maternity = it }, label = { Text("Maternidade") }, enabled = !saving)
            Text("Tipo de pré-natal", style = MaterialTheme.typography.labelLarge)
            Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                PrenatalOption("HABITUAL", prenatalType, !saving, Modifier.weight(1f)) { prenatalType = it }
                PrenatalOption("ALTO_RISCO", prenatalType, !saving, Modifier.weight(1f)) { prenatalType = it }
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = livesInBh, onCheckedChange = if (saving) null else { checked -> livesInBh = checked })
                Text("Mora em Belo Horizonte")
            }
            Text("Endereço (opcional)", style = MaterialTheme.typography.titleMedium)
            PatientAddressFields(address, { address = it }, enabled = !saving)
            Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                PartejarButton(
                    "Salvar",
                    onClick = {
                        attemptedSave = true
                        val parsedDueDate = dueDate.takeIf(String::isNotBlank)?.let(::parseBrazilianDate)
                        if (dueDate.isNotBlank() && parsedDueDate == null) return@PartejarButton
                        onSave(GestanteClinicalUpdate(
                            babyName.ifBlank { null },
                            parsedDueDate?.toString(),
                            maternity.ifBlank { null },
                            prenatalType,
                            livesInBh,
                            address
                        ))
                    },
                    enabled = !saving,
                    isLoading = saving,
                    modifier = Modifier.weight(1f)
                )
                PartejarButton(
                    "Cancelar",
                    onClick = onCancel,
                    enabled = !saving,
                    variant = PartejarButtonVariant.SECONDARY,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
private fun PrenatalOption(
    value: String,
    selected: String?,
    enabled: Boolean,
    modifier: Modifier,
    onSelected: (String) -> Unit
) {
    PartejarCard(
        onClick = { onSelected(value) },
        enabled = enabled,
        modifier = modifier
    ) {
        Text(
            value.toPrenatalLabel(),
            color = if (selected == value) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface
        )
    }
}
